/**
 * Modal component with Dashboard3 glass morphism styling
 *
 * @example
 * ```tsx
 * <Modal isOpen={isOpen} onClose={onClose} title="Edit Item">
 *   <ModalBody>Content here</ModalBody>
 * </Modal>
 * ```
 */

"use client";

import {
  Box,
  Modal as ChakraModal,
  type ModalProps as ChakraModalProps,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  type ModalContentProps,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useBreakpointValue,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import { MODAL_MAX_HEIGHT } from "@/lib/layout/viewport";
import { getPortaledSurfaceProps } from "@/lib/theme/portaled-surface";

type ResponsiveModalSize = { base: string; md?: string };

function normalizeModalSize(
  size: ModalProps["size"],
): ResponsiveModalSize | null {
  if (!size) return null;
  if (typeof size === "string") {
    return { base: size, md: size };
  }
  if (typeof size === "object" && size != null && "base" in size) {
    const responsive = size as ResponsiveModalSize;
    return { base: responsive.base, md: responsive.md ?? responsive.base };
  }
  return null;
}

export interface ModalProps extends ChakraModalProps {
  /**
   * Modal title
   */
  title?: string | ReactNode;
  /**
   * Modal size
   */
  size?: ChakraModalProps["size"];
  /**
   * Optional props passed to ModalContent (e.g. margins on mobile, Playwright hooks via `data-testid`).
   */
  contentProps?: ModalContentProps & { [key: `data-${string}`]: string };
  /**
   * When false, hides the top-right close button.
   */
  showCloseButton?: boolean;
}

export function Modal({
  title,
  children,
  size,
  contentProps,
  showCloseButton = true,
  ...props
}: ModalProps) {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const surfaceProps = getPortaledSurfaceProps(theme, interactive);

  const normalized = normalizeModalSize(size);
  const resolvedSize =
    useBreakpointValue(
      normalized ?? { base: "calc(100% - 32px)", md: "xl" },
      { ssr: false },
    ) ?? normalized?.base ?? "calc(100% - 32px)";

  const isFull = resolvedSize === "full";
  const chakraModalSize = isFull
    ? "full"
    : resolvedSize.startsWith("calc(")
      ? undefined
      : (resolvedSize as ChakraModalProps["size"]);

  const contentMaxW = isFull
    ? { base: "100%", md: "calc(100% - 32px)" }
    : resolvedSize.startsWith("calc(")
      ? resolvedSize
      : resolvedSize;

  return (
    <ChakraModal
      isCentered
      scrollBehavior="inside"
      size={chakraModalSize}
      {...props}
      // Chakra's hideOthers() console.errors when the portaled dialog is not
      // under document.body yet (Next.js 16 treats that as a blocking overlay).
      useInert={false}
    >
      <ModalOverlay
        bg={interactive.overlay.modal}
        backdropFilter={interactive.backdrop.blurOverlay}
      />
      <ModalContent
        borderRadius="12px"
        borderWidth="1px"
        borderColor={theme.borderGlass.default}
        boxShadow={theme.shadow.cardFeatured}
        maxW={contentMaxW}
        w={isFull ? "100%" : "calc(100% - 32px)"}
        mx={isFull ? { base: 0, md: "auto" } : { base: 4, md: "auto" }}
        my={{ base: 6, md: "auto" }}
        display="flex"
        flexDirection="column"
        maxH={MODAL_MAX_HEIGHT.content}
        overflow="hidden"
        minH={0}
        {...contentProps}
        {...surfaceProps}
      >
        {title && (
          <ModalHeader
            flexShrink={0}
            pb={3}
            pr={{ base: 12, md: 14 }}
            borderBottomWidth="1px"
            borderBottomColor={theme.borderGlass.default}
            fontSize={{ base: "xl", md: "2xl" }}
            color={theme.text.primary}
            fontWeight="600"
          >
            {title}
          </ModalHeader>
        )}
        {showCloseButton ? (
          <ModalCloseButton
            borderRadius="50%"
            top={{ base: 4, md: 5 }}
            right={{ base: 4, md: 5 }}
            zIndex={10}
            borderWidth="1px"
            borderColor="transparent"
            color={theme.text.secondary}
            data-testid="modal-close-button"
            sx={{
              "@media (hover: hover)": {
                "&:hover": {
                  color: theme.text.primary,
                  bg: interactive.neutral.bg,
                  borderColor: theme.borderGlass.default
                },
              },
              "@media (hover: none) and (pointer: coarse)": {
                "&:active": {
                  bg: interactive.neutral.bgStrong,
                },
              },
            }}
          />
        ) : null}
        <Box
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
          overflow="hidden"
          w="100%"
          sx={{
            "& > form": {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              width: "100%",
            },
            "& .chakra-modal__body": {
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
            },
            "& .chakra-modal__footer": {
              flexShrink: 0,
            },
          }}
        >
          {children}
        </Box>
      </ModalContent>
    </ChakraModal>
  );
}

/** Apply to ModalBody when overriding layout; base Modal already scrolls the body region. */
export const modalScrollBodyProps = {
  flex: "1",
  minH: 0,
  overflowY: "auto" as const,
  overflowX: "hidden" as const,
};

/** Apply to ModalFooter when overriding layout; base Modal already pins the footer. */
export const modalStickyFooterProps = {
  flexShrink: 0,
};

export {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
};
export type { ModalContentProps };
